import FormService from '../../../core/services/FormService';
import FetchService from '../../../core/services/FetchService';
import NotificationService from '../../../core/services/NotificationService';
import { extendObservable } from 'mobx';
import { browserHistory } from 'react-router';
import { omit, pickBy } from 'lodash';
import ContractProductsService from './ContractProductsService';
import ConfirmationDialogService from '../../../core/services/ConfirmationDialogService';
import moment from 'moment';
import { CONTRACT_PRODUCTS } from '../../../core/consts';

class UpgradeContractFormService {
  form = new FormService();
  fetch = new FetchService();
  submit = new FetchService();

  productsService = new ContractProductsService();

  constructor() {
    extendObservable(this, {
      contractId: '',
      contract: {},
      proportionalUseProduct: {},
    });
    this.form.validations = {};
  }

  init = (contractId) => {
    this.form.setInitialValues({});
    this.form.reset();
    this.contractId = contractId;
    this.load(contractId);
  };

  handleProportionalUse = (type) => {
    if (type === 'UPFRONT') {
      this.fetch
        .fetch({
          method: 'get',
          url: `/contracts/${this.contractId}/upgrade-upfront-products-preview`,
          apiVersion: 'v2',
        })
        .then(() => {
          if (this.fetch.data) {
            this.productsService.contractProducts = this.fetch.data.map((product, index) => ({
              rowKey: index,
              ...product,
            }));
          }
        });
    }
  };

  load = (contractId) => {
    this.contractId = contractId;
    this.fetch.fetch({ url: `/contracts/${this.contractId}`, apiVersion: 'v2' }).then(() => {
      if (this.fetch.data) {
        this.contract = {
          ...pickBy(this.fetch.data, (value) => value !== null && value !== undefined),
          startsAt: moment(this.fetch.data.startsAt).utc(),
          ...(this.fetch.data.endsAt && { endsAt: moment(this.fetch.data.endsAt).utc() }),
          products: this.fetch.data.products.map((product) => ({
            ...product,
            rowKey: product.id,
            ...(product.beginningOfTerm && { beginningOfTerm: moment(product.beginningOfTerm).add(3, 'hours') }),
          })),
        };

        this.productsService.init(this.fetch.data.type);
        this.productsService.load();
      }
    });
  };

  handleUpgrade = () => {
    this.form.submitted = true;
    if (this.form.errors) {
      NotificationService.addNotification('Fill the required fields', 'error');
      return;
    }

    if (!this.productsService.contractProducts.length) {
      NotificationService.addNotification('At least one product must be registered to upgrade the contract.', 'error');
      return;
    }

    const formattedType = this.contract.type.replace('_', ' ');
    ConfirmationDialogService.show(
      'Upgrade Contract',
      `${formattedType}: Are you sure that you want to upgrade the contract? Once you confirm it, the upgrade will take effect in the next cycle.`,
      () => {
        const isUsingCustomPackage = this.productsService.contractProducts.some(
          (product) => product.hs_sku === CONTRACT_PRODUCTS.CUSTOM_PACKAGE
        );
        this.submit
          .fetch({
            method: 'put',
            url: `/contracts/${this.contractId}/upgrade`,
            apiVersion: 'v3',
            body: {
              ...this.form.getValues(),
              ...(this.form.getValue('startsAt') && {
                startsAt: this.form.getValue('startsAt').utc().startOf('month'),
              }),
              ...(this.form.getValue('endsAt') && {
                endsAt: this.form.getValue('endsAt').utc().endOf('month'),
              }),
              products: this.productsService.contractProducts
                .filter((product) => {
                  if (isUsingCustomPackage) {
                    return product.isEnabled;
                  }
                  return true;
                })
                .map((product) => ({
                  ...omit(product, ['rowKey', 'id']),
                  description: product.description,
                  hs_sku: product.hs_sku,
                  amount: product.amount ? Number(product.amount) : undefined,
                  freeAmount: product.freeAmount ? Number(product.freeAmount) : undefined,
                  finalUnitPrice: Number(product.finalUnitPrice),
                  installments: product.installments ? Number(product.installments) : undefined,
                  paidInstallments: product.paidInstallments ? Number(product.paidInstallments) : undefined,
                  ...(product.discount && {
                    discount: Number(product.discount),
                  }),
                })),
            },
          })
          .then(() => {
            if (this.submit.data) {
              NotificationService.addNotification(`Contract successfully upgraded.`, 'success');
              browserHistory.push(`/contracts/${this.submit.data.id}/detail`);
            }
            if (this.submit.error) {
              NotificationService.addNotification(this.submit.error || `Error upgrading Contract.`, 'error');
            }
          });
      },
      'Yes',
      'No'
    );
  };
}

const upgradeContractFormService = new UpgradeContractFormService();

export default upgradeContractFormService;
